/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { getSelectedProductIdFromActionInput, getSelectedVariant, SelectedVariantInput } from '@msdyn365-commerce-modules/retail-actions';
/**
 * GetCmsProductAttributeContent Input Action
 */
export class GetCmsProductAttributeContentInput implements Msdyn365.IActionInput {
    public productId: number;

    public channelId: number;

    public constructor(productId: number | string, channelId: number) {
        this.productId = typeof productId === 'string' ? +productId : productId;
        this.channelId = channelId;
    }

    // TODO: Determine if the results of this get action should cache the results and if so provide
    // a cache object type and an appropriate cache key
    public getCacheKey = () => `CMS_Prod_Attr`;
    public getCacheObjectType = () => 'CMS_Prod_Attr';
    public dataCacheType = (): Msdyn365.CacheType => 'request';
}

// TODO: Create a data model here or import one to capture the response of the action
export interface IGetCmsProductAttributeContentData {
    id: string;
    type: string;
    title: string;
    prod_attr_top: string;
    prod_attr_main: string;
}

/**
 * TODO: Use this function to create the input required to make the action call
 */
const createInput = (args: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    const productId = getSelectedProductIdFromActionInput(args);

    if (productId) {
        return new GetCmsProductAttributeContentInput(+productId, +args.requestContext.apiSettings.channelId);
    }
    throw new Error('Unable to create createInput, no productId found on module config or query');
};

/**
 * TODO: Use this function to call your action and process the results as needed
 */
async function action(
    input: GetCmsProductAttributeContentInput,
    ctx: Msdyn365.IActionContext
): Promise<IGetCmsProductAttributeContentData | undefined> {
    const selectedVariantInput = new SelectedVariantInput(input.productId, input.channelId);
    try {
        const productResult = await getSelectedVariant(selectedVariantInput, ctx);
        if (productResult && productResult?.ItemId !== undefined) {
            // const frontApiEndpoint = 'http://herbal-cms.localhost:8001';
            const frontApiEndpoint = ctx.requestContext.app.config.cmsAPIEndpoint && ctx.requestContext.app.config.cmsAPIEndpoint;
            const requestUrl = `${frontApiEndpoint}/api/get-product-attribute/${productResult?.ItemId}/en`;

            const result = await Msdyn365.sendRequest<IGetCmsProductAttributeContentData>(requestUrl, 'get')
                .then(response => {
                    return response.data;
                })
                .catch(error => {
                    ctx.trace('[GetCmsProductAttributeContentInput] Failed to get cms detail.');
                    throw error;
                });
            return result;
        }

        return undefined;
    } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- cann't assign type to the error variable.
        ctx.telemetry.exception(error);
        ctx.telemetry.debug('[getCMSProductAttribute]Error executing action');
        return undefined;
    }
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IGetCmsProductAttributeContentData>>action,
    // TODO: Give your data action a unique id
    id: 'GetCmsProductAttributeContent',
    input: createInput
    // TODO: Uncomment the below line if this is a meant to be a batched data action
    // isBatched: true
});
